import { Component, EventEmitter, Inject, Input, OnChanges, Optional, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow } from '@app/core/components/grid/grid.types';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { Base64UrlService } from '@app/services/base64url.service';

@GridModalComponent('productsconfig')
@Component({
    selector: 'tt-products-config',
    templateUrl: './products-config.component.html',
    styleUrls: ['./products-config.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class ProductsConfigComponent extends ComponentBaseComponent implements OnChanges {
    public SelectTypes = SelectType;

    @Input()
    public params?: ProductsConfigParams;

    public get?: ProductsConfigGet;

    @Output()
    public getLoaded = new EventEmitter<ProductsConfigGet>();

    public questionList: QuestionConfigItem[] = [];

    public products: any[] = [];

    public resultRows: any[] = [];

    public configurating = false;

    public saving = false;

    public saveDisabled = true;

    public grid?: GridOptions;

    @Output()
    public submit = new EventEmitter<ProductsConfigGet>();

    constructor(private datatask: DataTaskService, private modal: ModalService, private base64: Base64UrlService, @Inject(TT_STATE_DIALOG) @Optional() stateDialog?: StateDialogContext) {
        super();
        this.initFromDialog(stateDialog);
    }

    public trackBy(_: number, item: QuestionConfigItem) {
        return `${item.choice_prod_id}|${item.question_type}|${item.choice_keyno}|${item.orderby}`;
    }

    public async configure() {
        this.configurating = true;

        try {
            delete this.grid; // the grid has validation modal responses which makes it fail, thus its safer to just rebuild the whole ass grid each time instead.

            setTimeout(this.createGrid);
        } finally {
        }
    }
    private createGrid = () => {
        const config = { ...this.get, products: this.products, questions: this.questionList };

        this.grid = new Grid()
            .setRememberId('w_productsconfig.grid')
            .setLoadDataFunction({ method: 3816, parameters: () => config })
            .setCSSOptions({ textWrapping: true })
            .setKendoConfig({ height: null, pageable: false, filterable: false });
    };

    private async getProductsConfigKitKeyno(): Promise<{ kit_keyno: `${number}` | number }> {
        return (await this.datatask.Post(1574))[0];
    }

    public async save() {
        if (!this.get) return;

        this.saving = true;
        this.get.kit_keyno = (await this.getProductsConfigKitKeyno()).kit_keyno;
        const progressModal = this.modal.openProgressDialog({ value: 0, max: this.suggestedOrderlines.length, disableClose: true });

        try {
            for (const line of this.suggestedOrderlines) {
                const { errorcode, errormessage } = await this.addConfig({ ...(line as any), argtype: this.get?.argtype, headno: this.get.headno, lineno: this.get.lineno, kit_keyno: this.get.kit_keyno });

                if (errorcode !== '0') {
                    progressModal.dialogRef.close();
                    this.modal.show({ type: 'danger', title: 'warning', translateTitle: true, message: errormessage });
                    break;
                } else {
                    progressModal.data.value += 1;
                    progressModal.updateValue(progressModal.data.value);
                }
            }

            if (progressModal.data.value === progressModal.data.max) {
                progressModal.updateStatus('finish');
                progressModal.data.disableClose = false;
                progressModal.dialogRef.disableClose = false;
                this.submit.emit(this.get);
            }
        } catch (error) {
            progressModal.dialogRef.close();
            console.log(error);
        } finally {
            this.saving = false;
        }
    }

    private async addConfig(config: ProductsConfigAddParams) {
        return (await this.datatask.Post(1126, config))[0];
    }

    private suggestedOrderlines: GridRow[] = [];

    public search = async (params: any): Promise<Item[]> => {
        return this.datatask.Post(1117, params);
    };

    public onSearchSelected(item: QuestionConfigItem, event?: Item) {
        item.chosen_prod_id = !!event ? '' + event.item_id : '0';

        this.onChanges(item.chosen_prod_id, item);
    }

    public onRead() {
        this.configurating = false;
        this.saveDisabled = true;

        const error = this.grid?.gridfunc?.getAllRows().find((row) => row['errorcode'] != 0);

        if (!!error) {
            this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: error['errormessage'] });
        } else if ((this.grid?.gridfunc?.getAllRows() ?? []).length > 0) {
            this.saveDisabled = false;
            this.suggestedOrderlines = this.grid!.gridfunc!.getAllRows();
        }
    }

    public onChanges(event: string, question: QuestionConfigItem) {
        if (question.question_type === QuestionType.All) {
            this.saveDisabled = true;
            this.updateAllQuestionsWithThisChoiceProdId(event, question.choice_prod_id);
        } else if (question.question_type === QuestionType.Normal) {
            this.saveDisabled = true;
        } else if (question.question_type === QuestionType.Question) {
            if (event.endsWith('YN_n')) {
                this.updateToValidDefaultChoicesFromChoiceProdId(question.from_choice_prod_id);
            } else if (event.endsWith('YN_y')) {
                const allValue = this.questionList.find((q) => q.question_type === QuestionType.All && q.choice_prod_id === question.from_choice_prod_id)?.chosen_prod_id ?? '';

                this.updateAllQuestionsWithThisChoiceProdId(allValue, question.from_choice_prod_id);
            }
        }

        this.resetVisibility();
    }

    private updateQuestionsFromYesNoQuestions() {
        const allYesNoQuestions = this.questionList.filter((q) => q.question_type === QuestionType.Question);

        for (const question of allYesNoQuestions) {
            if (question.chosen_prod_id.endsWith('YN_n')) {
                this.updateToValidDefaultChoicesFromChoiceProdId(question.from_choice_prod_id);
            } else if (question.chosen_prod_id.endsWith('YN_y')) {
                const allValue = this.questionList.find((q) => q.question_type === QuestionType.All && q.choice_prod_id === question.from_choice_prod_id)?.chosen_prod_id ?? '';

                this.updateAllQuestionsWithThisChoiceProdId(allValue, question.from_choice_prod_id);
            }
        }
    }

    private resetVisibility(): void {
        this.questionList = this.questionList.slice().sort((a, b) => +a.orderby - +b.orderby);

        for (const question of this.questionList) {
            question.isvisible = this.isConditionMet(question);
        }
    }

    private isConditionMet(question: QuestionConfigItem): '1' | '0' {
        const rules = question.dep_rules ?? [];
        const allConds = rules.flatMap((r) => r?.condition ?? []).filter((c) => c?.val);
        if (allConds.length === 0) return '1';

        const earlier = this.questionList.filter((p) => +p.orderby < +question.orderby);
        const visible = allConds.some((cond) => earlier.some((prev) => prev?.chosen_prod_id === cond.val));
        return visible ? '1' : '0';
    }

    private updateAllQuestionsWithThisChoiceProdId(value: string, choiceProdId: string) {
        this.questionList.filter((q) => q.choice_prod_id === choiceProdId).forEach((q) => (q.chosen_prod_id = value));
    }

    private async loadGet() {
        this.get = (await this.datatask.Post(1115, this.params))[0];
        this.getLoaded.emit(this.get);
        this.products = JSON.parse(this.base64.Decode(this.get!.productsconfig_selected));
    }

    private async loadQuestionList() {
        if (!!this.get) {
            this.questionList = await this.datatask.Post(1116, this.get);
            this.updateQuestionsFromYesNoQuestions();
        }
    }

    private updateToValidDefaultChoicesFromChoiceProdId(choiceProdId: string) {
        const choicesToUpdate = this.questionList.filter((question) => question.choice_prod_id === choiceProdId && question.question_type === QuestionType.Normal);
        console.log(choicesToUpdate);
        for (const question of choicesToUpdate) {
            if (question.question_type === QuestionType.Normal) {
                question.chosen_prod_id = !!question.chosen_prod_id && question.selectlistrecords.some((item) => item.item_id === question.chosen_prod_id) ? question.chosen_prod_id : question.selectlistrecords[0]?.item_id;
            }
        }

        console.log(this.questionList);
    }

    private async init() {
        await this.loadGet();
        await this.loadQuestionList();
        this.resetVisibility();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params'] && 'argtype' in changes['params'].currentValue && ('headno' in changes['params'].currentValue || 'lineno' in changes['params'].currentValue)) {
            this.init();
        }
    }

    private async initFromDialog(stateDialog?: StateDialogContext) {
        if (!!stateDialog?.params && 'argtype' in stateDialog.params && ('headno' in stateDialog.params || 'lineno' in stateDialog.params)) {
            this.params = stateDialog.params as unknown as ProductsConfigParams;
            this.submit.subscribe({ next: () => stateDialog.close(this.get) });
            stateDialog.setSize({ width: 'min(80rem, 90vw)', height: '90vh' });
            await this.init();
            stateDialog.setHeading(this.get?.webpage_heading ?? '');
        }
    }
}

export interface ProductsConfigParams {
    /**
     * The entity to configure products for, either 'orderhead', 'orderline', 'purchhead' or 'purchline'.
     */
    argtype: 'orderline' | 'purchline';

    /**
     * The internal no of either the orderhead or purchhead to configue products for.
     */
    headno: `${number}` | number;

    /**
     * The line keyno of the orderline or purchline to configure product for.
     */
    lineno: `${number}` | number;
}

interface ProductsConfigAddParams {
    prod_id: string;
    prodsize_keyno: string;
    prodchoice_keyno: string;
    quantity: string;
    input_price_ordval: string;
    input_disc: string;
    input_amount_ordval: string;
    argtype: string;
    headno: `${number}` | number;
    lineno: `${number}` | number;
    kit_keyno: `${number}` | number;
}

export interface ProductsConfigGet {
    argtype: string;
    headno: `${number}` | number;
    lineno: `${number}` | number;
    productsconfig_selected: string;
    heading_selected_products: string;
    heading_configuration: string;
    finish_item_state: string;
    finish_item_parms: string;
    show_setting: '1' | '0';
    kit_keyno: `${number}` | number;
    webpage_heading: string;
}

interface QuestionConfigItem {
    item_default?: string;
    choice_prod_id: string;
    choice_prod_name: string;
    chosen_prod_id: string;
    orderby: `${number}` | number;
    chosen_searchstring: string;
    nbr_of_choices: `${number}` | number;
    selecttype: SelectType;
    question_type: QuestionType;
    sublabel: string;
    selectlistrecords: SelectOptionItem[];
    restriction_prod_id: string;
    from_choice_prod_id: string;
    choice_keyno: `${number}` | number;

    /**
     * Dependency rules.
     */
    dep_rules?: DepRuleItem[];

    /**
     * Whether the item is hidden by a condition in dependency rules.
     */
    isvisible: '1' | '0';

    /**
     * Whether the item is hidden by default from list get.
     */
    hide: '1' | '0';
}

interface DepRuleItem {
    condition?: ConditionItem[];
}

interface ConditionItem {
    val?: string;
    ok?: '1' | '0';
}

interface SelectOptionItem {
    item_name: string;
    item_id: string;
}

enum SelectType {
    Search = 'S',
    DropDown = 'D',
    Radio = 'R',
}

enum QuestionType {
    /**
     * Regular question types as listed in choice.
     */
    Normal = 'N',

    /**
     * Questions with this question type will affect all items that share the same choice_prod_id.
     */
    All = 'A',

    /**
     * Question types (y/n).
     */
    Question = 'S',
}

const QUESTIONLIST = [
    {
        choice_prod_id: 'EPOXY__YN',
        choice_prod_name: 'Samme valg for alle Farge',
        chosen_prod_id: 'EPOXY__YN_y',
        orderby: '1',
        chosen_searchstring: '',
        nbr_of_choices: '2',
        selecttype: 'R',
        question_type: 'S',
        sublabel: '',
        selectlistrecords: [
            {
                item_id: 'EPOXY__YN_y',
                item_name: 'Ja',
            },
            {
                item_id: 'EPOXY__YN_n',
                item_name: 'Nei',
            },
        ],
        restriction_prod_id: '',
        from_choice_prod_id: 'EPOXY',
        choice_keyno: '0',
        dep_rules: [],
        isvisible: '1',
        hide: '0',
    },
    {
        choice_prod_id: 'EPOXY',
        choice_prod_name: 'Farge Alle',
        chosen_prod_id: '',
        orderby: '2',
        chosen_searchstring: '',
        nbr_of_choices: '16',
        selecttype: 'D',
        question_type: 'A',
        sublabel: '',
        selectlistrecords: [
            {
                item_id: 'FNRKU',
                item_name: 'Kufiolilla',
            },
            {
                item_id: 'FNRTOSCANA',
                item_name: 'Lys Toscana',
            },
            {
                item_id: 'FNRROSA',
                item_name: 'ROSA',
            },
            {
                item_id: 'FNRSKRAA',
                item_name: 'Skrå striper i lilla',
            },
        ],
        restriction_prod_id: '2kontmedbord',
        from_choice_prod_id: 'EPOXY',
        choice_keyno: '0',
        dep_rules: [
            {
                condition: [
                    {
                        val: 'EPOXY__YN_y',
                        ok: '0',
                    },
                ],
            },
        ],
        isvisible: '1',
        hide: '0',
    },
    {
        choice_prod_id: 'EPOXY',
        choice_prod_name: 'Farge',
        chosen_prod_id: '',
        orderby: '3',
        chosen_searchstring: '',
        nbr_of_choices: '16',
        selecttype: 'D',
        question_type: 'N',
        sublabel: 'Kontraktsstol',
        selectlistrecords: [
            {
                item_id: 'FNRKU',
                item_name: 'Kufiolilla',
            },
            {
                item_id: 'FNRTOSCANA',
                item_name: 'Lys Toscana',
            },
            {
                item_id: 'FNRROSA',
                item_name: 'ROSA',
            },
            {
                item_id: 'FNRSKRAA',
                item_name: 'Skrå striper i lilla',
            },
        ],
        restriction_prod_id: '2kontmedbord',
        from_choice_prod_id: 'EPOXY',
        choice_keyno: '429900',
        dep_rules: [
            {
                condition: [
                    {
                        val: 'EPOXY__YN_n',
                        ok: '0',
                    },
                ],
            },
        ],
        isvisible: '1',
        hide: '0',
    },
    {
        choice_prod_id: 'EPOXY',
        choice_prod_name: 'Farge',
        chosen_prod_id: '',
        orderby: '4',
        chosen_searchstring: '',
        nbr_of_choices: '16',
        selecttype: 'D',
        question_type: 'N',
        sublabel: 'Kontraktsstol',
        selectlistrecords: [
            {
                item_id: 'FNRKU',
                item_name: 'Kufiolilla',
            },
            {
                item_id: 'FNRTOSCANA',
                item_name: 'Lys Toscana',
            },
            {
                item_id: 'FNRROSA',
                item_name: 'ROSA',
            },
            {
                item_id: 'FNRSKRAA',
                item_name: 'Skrå striper i lilla',
            },
        ],
        restriction_prod_id: '2kontmedbord',
        from_choice_prod_id: 'EPOXY',
        choice_keyno: '430000',
        dep_rules: [
            {
                condition: [
                    {
                        val: 'EPOXY__YN_n',
                        ok: '0',
                    },
                ],
            },
        ],
        isvisible: '1',
        hide: '0',
    },
    {
        choice_prod_id: 'EPOXY',
        choice_prod_name: 'Farge',
        chosen_prod_id: '',
        orderby: '5',
        chosen_searchstring: '',
        nbr_of_choices: '16',
        selecttype: 'D',
        question_type: 'N',
        sublabel: 'Trådhylle',
        selectlistrecords: [
            {
                item_id: 'FNRKU',
                item_name: 'Kufiolilla',
            },
            {
                item_id: 'FNRTOSCANA',
                item_name: 'Lys Toscana',
            },
            {
                item_id: 'FNRROSA',
                item_name: 'ROSA',
            },
            {
                item_id: 'FNRSKRAA',
                item_name: 'Skrå striper i lilla',
            },
        ],
        restriction_prod_id: '2kontmedbord',
        from_choice_prod_id: 'EPOXY',
        choice_keyno: '456500',
        dep_rules: [
            {
                condition: [
                    {
                        val: 'EPOXY__YN_n',
                        ok: '0',
                    },
                ],
            },
        ],
        isvisible: '1',
        hide: '0',
    },
    {
        choice_prod_id: 'EPOXY',
        choice_prod_name: 'Farge',
        chosen_prod_id: '',
        orderby: '6',
        chosen_searchstring: '',
        nbr_of_choices: '16',
        selecttype: 'D',
        question_type: 'N',
        sublabel: 'Trådhylle',
        selectlistrecords: [
            {
                item_id: 'FNRKU',
                item_name: 'Kufiolilla',
            },
            {
                item_id: 'FNRTOSCANA',
                item_name: 'Lys Toscana',
            },
            {
                item_id: 'FNRROSA',
                item_name: 'ROSA',
            },
            {
                item_id: 'FNRSKRAA',
                item_name: 'Skrå striper i lilla',
            },
        ],
        restriction_prod_id: '2kontmedbord',
        from_choice_prod_id: 'EPOXY',
        choice_keyno: '456600',
        dep_rules: [
            {
                condition: [
                    {
                        val: 'EPOXY__YN_n',
                        ok: '0',
                    },
                ],
            },
        ],
        isvisible: '1',
        hide: '0',
    },
];
